import { Alert, Anchor, Box, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

// The verdicts have opposite remedies - one says delete a DNS record, one says publish
// one - so they are never collapsed into a single "misconfigured" state, the same way
// ClickTrackingHostHealth keeps its probe states apart. `healthy` picks the same
// green/red treatment the DNS rows use in DnsRecordsTable. A failed lookup is
// deliberately absent from this map: it is neither valid nor misconfigured, so it
// renders as a plain note below instead of a verdict, and raises nothing on the
// Emails tab.
const MAIL_STATES = {
  valid: {
    healthy: true,
    title: 'Replies to your sender domain reach your own mailbox',
    advice:
      'Mail for this domain is accepted by your own mail provider, so replies and out-of-office messages from your recipients get through. Nothing to do here.',
  },
  deliveredToReleva: {
    healthy: false,
    title: 'Mail for your sender domain is delivered to Releva',
    advice:
      "This domain's mail is routed to our bounce handler, which accepts only bounce and abuse messages and refuses everything else - so every reply and every out-of-office message your recipients send to your From or Reply-to address is being rejected instead of reaching you.",
    // The CNAME hijacks the whole domain, so this is true regardless of what Reply-to is
    // set to - unlike noMx below, where the domain that actually needs an MX may not be
    // the one Reply-to points at.
    consequence: 'Replies your recipients send to this domain are not reaching you.',
  },
  noMx: {
    healthy: false,
    title: 'No mail server is published for your sender domain',
    advice:
      'This domain has no MX record, so senders fall back to its A record - usually the web server, which does not accept mail - and replies bounce. Publish an MX record for whichever provider hosts that mailbox, or set your Reply-to address to a domain that already has one.',
    // Deliberately does not say "replies are not reaching you": the domain's own advice
    // above offers moving Reply-to to a domain that does have an MX, and this check only
    // resolves the sender domain - it cannot see whether that was already done.
    consequence: 'Mail sent to your sender address bounces - there is no mail server to receive it.',
  },
};

// Where mail addressed to the sender domain actually lands, which neither required DNS
// record can see: both of those check a name Releva asked for, and this failure is an
// extra record the merchant published at the sender domain itself. The verdict rides in
// on the verificationStatus payload the parent already fetched, so - unlike
// ClickTrackingHostHealth - there is no probe of its own, no spinner and no re-check
// button of its own. Reloading the settings tab re-fetches verificationStatus and so gets
// a fresh verdict; the modal's own "Re-check DNS Records" button does not, since it only
// renders while isEmailSendingVerified is false - not the incident this panel exists for,
// where sending is already verified but mail still routes to Releva.
const SenderDomainMailHealth = ({ check, senderDomain }) => {
  // The API computing this check may not be deployed yet. Say nothing at all rather
  // than reporting an unknown verdict on every domain.
  if (!check) {
    return null;
  }

  // `unknown` lands here, and so does a verdict newer than this build: neither is a
  // green or a red state, so say plainly that there is no answer yet.
  const mailState = MAIL_STATES[check.state];
  if (!mailState) {
    return (
      <Text size="xs" c="dimmed" mt="xs">
        Could not look up where mail for your sender domain is delivered. We will try again on the next check - there is
        nothing for you to fix.
      </Text>
    );
  }

  return (
    <Box
      mt="xs"
      p="sm"
      style={{
        backgroundColor: mailState.healthy ? 'var(--mantine-color-success-3)' : 'var(--mantine-color-danger-3)',
      }}
    >
      <Text size="sm" fw={500}>
        {mailState.title}
      </Text>
      <Text size="xs">{mailState.advice}</Text>
      {/* A CNAME at the sender domain itself is what drags the domain's whole mail
          delivery onto our host, so name the record to delete rather than only the
          symptom. Only shown for that verdict: for the others the CNAME target, if
          there is one, is not what needs changing. */}
      {check.state === 'deliveredToReleva' && check.cname && senderDomain && (
        <Text size="xs" mt={4} style={{ wordBreak: 'break-all' }}>
          {senderDomain} is a CNAME to {check.cname}. Remove that record - only the click-tracking host shown above
          should point at Releva, never the sender domain itself.
        </Text>
      )}
    </Box>
  );
};

// The Emails tab is where the Reply-to address is set, and a merchant will not open the
// verification modal unless something prompts them - so a failing verdict gets one
// sentence and a link here, with the record-level advice left to the modal. A valid
// verdict and an unreadable one both stay silent: neither is something to act on.
export const SenderDomainMailAlert = ({ check, onShowDetails }) => {
  const mailState = MAIL_STATES[check?.state];
  if (!mailState || mailState.healthy) {
    return null;
  }

  return (
    <Alert color="red" icon={<IconAlertTriangle size={16} />} title={mailState.title} mt="xs">
      <Text size="sm">
        {mailState.consequence}{' '}
        <Anchor
          size="sm"
          component="button"
          onClick={(e) => {
            e.preventDefault();
            onShowDetails();
          }}
        >
          What to change...
        </Anchor>
      </Text>
    </Alert>
  );
};

export default SenderDomainMailHealth;
